import { getFeaturedReviewsForToday } from '../data/featuredReviews';
import { StarIcon } from './icons/StarIcon';

export default function FeaturedReviews() {
  const reviews = getFeaturedReviewsForToday();

  return (
    <section className="py-12 px-4 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            ✨ Featured Reviews
          </h2>
          <p className="text-slate-600">
            Fresh editorial picks from trusted sources. Rotates daily.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <a
              key={review.id}
              href={review.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group h-full"
            >
              <div className="featured-card p-6 h-full flex flex-col hover:scale-105 transition-transform duration-300">
                {/* Header with source */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {review.sourceIcon} {review.sourceName}
                  </span>
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded ${
                      review.sentiment === 'positive'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {review.sentiment === 'positive' ? '✓ Recommended' : '△ Mixed'}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
                  {review.title}
                </h3>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarIcon
                        key={i}
                        filled={i < Math.floor(review.rating)}
                        half={
                          i === Math.floor(review.rating) &&
                          review.rating % 1 !== 0
                        }
                      />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-slate-700">
                    {review.rating.toFixed(1)}/5
                  </span>
                </div>

                {/* Verdict */}
                <p className="text-sm font-semibold text-sky-600 mb-3 italic">
                  "{review.verdict}"
                </p>

                {/* Excerpt */}
                <p className="text-sm text-slate-600 mb-4 line-clamp-3 flex-grow">
                  {review.excerpt}
                </p>

                {/* Pros/Cons Summary */}
                <div className="space-y-2 mb-4 text-sm">
                  <div>
                    <span className="font-semibold text-green-700">Pros:</span>
                    <span className="text-slate-600 ml-2">
                      {review.pros.slice(0, 2).join(', ')}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-amber-700">Cons:</span>
                    <span className="text-slate-600 ml-2">
                      {review.cons.slice(0, 1).join(', ')}
                    </span>
                  </div>
                </div>

                {/* CTA */}
                <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Read full review</span>
                  <span className="text-sky-600 font-semibold group-hover:text-sky-700">
                    →
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Featured reviews refresh daily. Find more with the search above.</p>
        </div>
      </div>

      <style>{`
        .featured-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 249, 255, 0.5) 100%);
          border: 1px solid rgba(71, 85, 105, 0.1);
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
          backdrop-filter: blur(10px);
        }

        .featured-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.03);
          border-color: rgba(71, 85, 105, 0.2);
        }
      `}</style>
    </section>
  );
}
